import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import CodeBlock from './CodeBlock';
import ParameterTable from './ParameterTable';
import ApiTester from './ApiTester';

const ContentSection = ({ activeSection }) => {
  const getSectionContent = () => {
    switch (activeSection) {
      case 'getting-started-overview':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">EmailValidator API Documentation</h1>
              <p className="text-lg text-text-secondary mb-6">
                Welcome to the EmailValidator API documentation. Our REST API provides powerful email validation 
                services to help you maintain clean email lists and improve deliverability rates.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-surface border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Zap" size={24} className="text-primary" />
                  <h3 className="text-lg font-semibold text-text-primary">Fast & Reliable</h3>
                </div>
                <p className="text-text-secondary">
                  Process thousands of emails per minute with 99.9% uptime guarantee and global CDN distribution.
                </p>
              </div>

              <div className="bg-surface border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Shield" size={24} className="text-success" />
                  <h3 className="text-lg font-semibold text-text-primary">Secure & Private</h3>
                </div>
                <p className="text-text-secondary">
                  Enterprise-grade security with SOC 2 compliance and GDPR-ready data processing.
                </p>
              </div>

              <div className="bg-surface border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Code" size={24} className="text-accent" />
                  <h3 className="text-lg font-semibold text-text-primary">Easy Integration</h3>
                </div>
                <p className="text-text-secondary">
                  RESTful API with comprehensive SDKs for popular programming languages and frameworks.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-blue-900 mb-2">Base URL</h4>
                  <code className="text-sm bg-white px-3 py-2 rounded border">
                    https://api.octalchip.com
                  </code>
                  <p className="text-blue-800 mt-2">
                    All API requests should be made to this base URL with the appropriate endpoint path.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'getting-started-quick-start':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Quick Start Guide</h1>
              <p className="text-lg text-text-secondary mb-6">
                Get started with the EmailValidator API in just a few minutes. Follow these simple steps 
                to make your first API call.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Get Your JWT Token</h3>
                  <p className="text-text-secondary mb-4">
                    Sign up for a free account and get your JWT token from the browser's local storage.
                  </p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Step-by-Step Instructions:</h4>
                    <ol className="text-blue-800 space-y-2 text-sm">
                      <li>1. <strong>Sign up/Login:</strong> Go to <a href="/auth/register" className="text-blue-600 underline">Register</a> or <a href="/auth/login" className="text-blue-600 underline">Login</a></li>
                      <li>2. <strong>Open Developer Tools:</strong> Press F12 or right-click → Inspect</li>
                      <li>3. <strong>Go to Application tab:</strong> Click on "Application" (Chrome) or "Storage" (Firefox)</li>
                      <li>4. <strong>Find Local Storage:</strong> Expand "Local Storage" → Click on your domain</li>
                      <li>5. <strong>Find Supabase session:</strong> Look for key starting with "sb-"</li>
                      <li>6. <strong>Copy access_token:</strong> Copy the long JWT token value</li>
                    </ol>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-green-900 mb-2">What You'll See in Developer Tools:</h4>
                    <div className="bg-white border rounded p-3 text-xs font-mono">
                      <div className="text-gray-600">Local Storage → http://localhost:4028</div>
                      <div className="mt-2">
                        <div className="text-blue-600">Key: <span className="text-gray-800">sb-paziwddtrxxreqewroyr-auth-token</span></div>
                        <div className="text-green-600">Value: <span className="text-gray-800">{"{"}</span></div>
                        <div className="ml-4 text-gray-800">
                          <div>"access_token": <span className="text-red-600">"eyJhbGciOiJIUzI1NiIs..."</span>,</div>
                          <div>"refresh_token": "2xjsp6h47y36",</div>
                          <div>"expires_in": 3600,</div>
                          <div>"token_type": "bearer"</div>
                        </div>
                        <div className="text-gray-800">{"}"}</div>
                      </div>
                    </div>
                    <p className="text-green-800 text-sm mt-2">
                      <strong>Copy the entire access_token value</strong> (the long string starting with "eyJ...")
                    </p>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Example JWT Token:</h4>
                    <code className="text-xs bg-yellow-100 px-2 py-1 rounded break-all">
                      eyJhbGciOiJIUzI1NiIsImtpZCI6IkFmazJVUno5ak5JVHI5WkgiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3Bheml3ZGR0cnh4cmVxZXdyb3lyLnN1cGFiYXNlLmNvL2F1dGgvdjEiLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMiLCJhdWQiOiJhdXRoZW50aWNhdGVkIiwiZXhwIjoxNzUzNjA5OTgzLCJpYXQiOjE3NTM2MDYzODMsImVtYWlsIjoicnVwYXJlbGl5YXJpbmFAZ21haWwuY29tIiwicGhvbmUiOiIiLCJhcHBfbWV0YWRhdGEiOnsicHJvdmlkZXIiOiJlbWFpbCIsInByb3ZpZGVycyI6WyJlbWFpbCJdfSwidXNlcl9tZXRhZGF0YSI6eyJlbWFpbCI6InJ1cGFyZWxpeWFyaW5hQGdtYWlsLmNvbSIsImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJmdWxsX25hbWUiOiJyaW5hIiwicGhvbmVfdmVyaWZpZWQiOmZhbHNlLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMifSwicm9sZSI6ImF1dGhlbnRpY2F0ZWQiLCJhYWwiOiJhYWwxIiwiYW1yIjpbeyJtZXRob2QiOiJwYXNzd29yZCIsInRpbWVzdGFtcCI6MTc1MzYwNjM4M31dLCJzZXNzaW9uX2lkIjoiNTVjOGQwYjUtODhiNS00MzNjLWE3N2MtNjIxMzdkYzQyZmY0IiwiaXNfYW5vbnltb3VzIjpmYWxzZX0.91yjOkKAzc495r6-u8ixdh6HvgsKhU6OBwZhR9HxB8M
                    </code>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-purple-900 mb-2">Test Your Token Immediately:</h4>
                    <p className="text-purple-800 text-sm mb-3">
                      Replace <code className="bg-purple-100 px-1 rounded">YOUR_JWT_TOKEN</code> with your actual token and test:
                    </p>
                    <CodeBlock
                      language="bash"
                      title="Quick Test Command"
                      code={`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer YOUR_JWT_TOKEN' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["test@example.com"],
    "validationType": "bulk"
}'`}
                    />
                    <p className="text-purple-800 text-sm mt-2">
                      <strong>Expected response:</strong> If successful, you'll get a JSON response with validation results.
                    </p>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-900 mb-2">Important Notes:</h4>
                    <ul className="text-orange-800 text-sm space-y-1">
                      <li>• <strong>Token Expiration:</strong> JWT tokens expire after 1 hour (3600 seconds)</li>
                      <li>• <strong>Auto Refresh:</strong> The web app automatically refreshes tokens</li>
                      <li>• <strong>Manual Refresh:</strong> If token expires, simply log in again to get a new one</li>
                      <li>• <strong>Security:</strong> Never share your JWT token publicly</li>
                    </ul>
                  </div>
                  
                  <Button variant="primary" iconName="ExternalLink" iconPosition="right" onClick={() => window.open('/auth/register', '_blank')}>
                    Get Started - Sign Up
                  </Button>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Make Your First Request</h3>
                  <p className="text-text-secondary mb-4">
                    Use cURL or your preferred HTTP client to validate an email address.
                  </p>
                  <CodeBlock
                    language="bash"
                    title="cURL Example"
                    code={`curl -X POST https://api.emailvalidator.com/v1/validate \\
  -H "Authorization: Bearer YOUR_API_KEY" \ -H"Content-Type: application/json" \\
  -d '{
    "email": "test@example.com"
  }'`}
                  />
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Handle the Response</h3>
                  <p className="text-text-secondary mb-4">
                    Process the JSON response to get validation results.
                  </p>
                  <CodeBlock
                    language="json"
                    title="Response Example"
                    code={`{
  "success": true,
  "data": {
    "email": "test@example.com",
    "valid": true,
    "score": 95,
    "domain": "example.com",
    "mx_records": true,
    "disposable": false,
    "role_account": false,
    "deliverable": "high"
  }
}`}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'authentication-api-keys':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">API Authentication</h1>
              <p className="text-lg text-text-secondary mb-6">
                The EmailValidator API supports two authentication methods: JWT Bearer tokens for web applications 
                and API keys for external integrations. Choose the method that best fits your use case.
              </p>
            </div>

            {/* Authentication Methods Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Key" size={24} className="text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">JWT Bearer Tokens</h3>
                </div>
                <p className="text-blue-800 mb-4">
                  Perfect for web applications and temporary integrations. Tokens expire after 1 hour to 1 week.
                </p>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Short lifespan (1 hour - 1 week)</li>
                  <li>• Auto-refreshed by web app</li>
                  <li>• Ideal for frontend applications</li>
                  <li>• Format: <code>Authorization: Bearer &lt;token&gt;</code></li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Shield" size={24} className="text-green-600" />
                  <h3 className="text-lg font-semibold text-green-900">API Keys</h3>
                </div>
                <p className="text-green-800 mb-4">
                  Perfect for external integrations and automation. Keys have unlimited lifespan until revoked.
                </p>
                <ul className="text-green-700 text-sm space-y-1">
                  <li>• Unlimited lifespan (until revoked)</li>
                  <li>• Perfect for automation</li>
                  <li>• Ideal for external integrations</li>
                  <li>• Format: <code>Authorization: ApiKey &lt;key&gt;</code></li>
                </ul>
              </div>
            </div>

            {/* JWT Bearer Token Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">JWT Bearer Token Authentication</h3>
              <p className="text-blue-800 mb-4">
                Use JWT Bearer tokens for web applications and temporary integrations. These tokens are automatically 
                refreshed by the web application.
              </p>
              
              <div className="bg-white border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">Header Format:</h4>
                <CodeBlock 
                  language="bash"
                  code={`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`}
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-900 mb-1">JWT Token Limitations:</h4>
                    <ul className="text-yellow-800 text-sm space-y-1">
                      <li>• Tokens expire after 1 hour to 1 week maximum</li>
                      <li>• Not suitable for long-running integrations</li>
                      <li>• Requires periodic token refresh</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* API Key Section */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-green-900 mb-4">API Key Authentication</h3>
              <p className="text-green-800 mb-4">
                Use API keys for external integrations, automation, and long-running applications. These keys have 
                unlimited lifespan until manually revoked.
              </p>
              
              <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-900 mb-2">Header Format:</h4>
                <CodeBlock 
                  language="bash"
                  code={`Authorization: ApiKey evapia1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6`}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">How to Get Your API Key:</h4>
                <ol className="text-blue-800 space-y-2 text-sm">
                  <li>1. <strong>Sign in to your account</strong> at <a href="/auth/login" className="text-blue-600 underline">Login</a></li>
                  <li>2. <strong>Go to Account Settings</strong> → Security section</li>
                  <li>3. <strong>Generate API Key</strong> in the API Key Management section</li>
                  <li>4. <strong>Copy and save</strong> the key securely (it won't be shown again)</li>
                </ol>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">API Key Benefits:</h4>
                    <ul className="text-green-800 text-sm space-y-1">
                      <li>• Unlimited lifespan until revoked</li>
                      <li>• Perfect for automation and integrations</li>
                      <li>• No need for token refresh logic</li>
                      <li>• Secure hashed storage</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-yellow-900 mb-2">Security Best Practices</h4>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• <strong>Never expose tokens/keys</strong> in client-side code, public repositories, or logs</li>
                    <li>• <strong>Use environment variables</strong> to store authentication credentials</li>
                    <li>• <strong>Rotate API keys regularly</strong> if you suspect they've been compromised</li>
                    <li>• <strong>Monitor usage patterns</strong> and revoke unused keys</li>
                    <li>• <strong>Use HTTPS only</strong> for all API communications</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Getting Your JWT Token</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2">From Browser Local Storage:</h4>
                  <ol className="text-blue-800 space-y-2 text-sm">
                    <li>1. Log in to your EmailValidator account</li>
                    <li>2. Open browser Developer Tools (F12)</li>
                    <li>3. Go to Application/Storage tab → Local Storage</li>
                    <li>4. Find the Supabase session data</li>
                    <li>5. Copy the <code className="bg-blue-100 px-1 rounded">access_token</code> value</li>
                  </ol>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Authentication Header</h3>
                <CodeBlock
                  language="bash"
                  title="Authorization Header"
                  code={`Authorization: Bearer eyJhbGciOiJIUzI1NiIsImtpZCI6IkFmazJVUno5ak5JVHI5WkgiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3Bheml3ZGR0cnh4cmVxZXdyb3lyLnN1cGFiYXNlLmNvL2F1dGgvdjEiLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMiLCJhdWQiOiJhdXRoZW50aWNhdGVkIiwiZXhwIjoxNzUzNjA5OTgzLCJpYXQiOjE3NTM2MDYzODMsImVtYWlsIjoicnVwYXJlbGl5YXJpbmFAZ21haWwuY29tIiwicGhvbmUiOiIiLCJhcHBfbWV0YWRhdGEiOnsicHJvdmlkZXIiOiJlbWFpbCIsInByb3ZpZGVycyI6WyJlbWFpbCJdfSwidXNlcl9tZXRhZGF0YSI6eyJlbWFpbCI6InJ1cGFyZWxpeWFyaW5hQGdtYWlsLmNvbSIsImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJmdWxsX25hbWUiOiJyaW5hIiwicGhvbmVfdmVyaWZpZWQiOmZhbHNlLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMifSwicm9sZSI6ImF1dGhlbnRpY2F0ZWQiLCJhYWwiOiJhYWwxIiwiYW1yIjpbeyJtZXRob2QiOiJwYXNzd29yZCIsInRpbWVzdGFtcCI6MTc1MzYwNjM4M31dLCJzZXNzaW9uX2lkIjoiNTVjOGQwYjUtODhiNS00MzNjLWE3N2MtNjIxMzdkYzQyZmY0IiwiaXNfYW5vbnltb3VzIjpmYWxzZX0.91yjOkKAzc495r6-u8ixdh6HvgsKhU6OBwZhR9HxB8M`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Example Request</h3>
                <CodeBlock
                  language="javascript"
                  title="JavaScript Example"
                  code={`const response = await fetch('https://api.octalchip.com/emailvalidator', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_JWT_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    emails: ['test@example.com'],
    validationType: 'bulk'
  })
});

const data = await response.json();
console.log(data);`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">cURL Example</h3>
                <CodeBlock
                  language="bash"
                  title="cURL with Bearer Token"
                  code={`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer YOUR_JWT_TOKEN' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["test@example.com"],
    "validationType": "bulk"
}'`}
                />
              </div>
            </div>
          </div>
        );

      case 'local-testing-curl-commands':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Local Testing with cURL</h1>
              <p className="text-lg text-text-secondary mb-6">
                Test the EmailValidator API locally using cURL commands. This guide shows you how to test 
                all endpoints on your local Supabase instance.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-blue-900 mb-2">Prerequisites</h4>
                  <ul className="text-blue-800 space-y-2">
                    <li>• Supabase CLI installed: <code className="bg-blue-100 px-2 py-1 rounded">npm install -g supabase</code></li>
                    <li>• Supabase project started: <code className="bg-blue-100 px-2 py-1 rounded">npx supabase start</code></li>
                    <li>• Local anon key from: <code className="bg-blue-100 px-2 py-1 rounded">npx supabase status</code></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">1. Single Email Validation</h3>
                <CodeBlock
                  language="bash"
                  title="cURL Command"
                  code={`curl -X POST "http://localhost:54321/functions/v1/validate-email-single" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ANON_KEY" \\
  -d '{
    "email": "test@example.com"
  }'`}
                />
                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Expected Response:</strong> Returns validation result for a single email address
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">2. Bulk Email Validation</h3>
                <CodeBlock
                  language="bash"
                  title="cURL Command"
                  code={`curl -X POST "http://localhost:54321/functions/v1/validate-email-bulk" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ANON_KEY" \\
  -d '{
    "emails": ["test@example.com", "user@gmail.com", "invalid@nonexistent.com"],
    "validationType": "bulk"
  }'`}
                />
                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Expected Response:</strong> Returns validation ID and summary for bulk processing
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">3. Get Validation Results</h3>
                <CodeBlock
                  language="bash"
                  title="cURL Command"
                  code={`curl -X GET "http://localhost:54321/functions/v1/get-validation-results?validationId=YOUR_VALIDATION_ID" \\
  -H "Authorization: Bearer YOUR_ANON_KEY"`}
                />
                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Expected Response:</strong> Returns detailed results for a completed validation
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">4. Get Validation Progress</h3>
                <CodeBlock
                  language="bash"
                  title="cURL Command"
                  code={`curl -X GET "http://localhost:54321/functions/v1/get-validation-progress?validationId=YOUR_VALIDATION_ID" \\
  -H "Authorization: Bearer YOUR_ANON_KEY"`}
                />
                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>Expected Response:</strong> Returns progress status for ongoing validation
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-yellow-900 mb-2">Important Notes</h4>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• Replace <code className="bg-yellow-100 px-2 py-1 rounded">YOUR_ANON_KEY</code> with your local Supabase anon key</li>
                    <li>• Replace <code className="bg-yellow-100 px-2 py-1 rounded">YOUR_VALIDATION_ID</code> with the ID returned from bulk validation</li>
                    <li>• Make sure Supabase is running: <code className="bg-yellow-100 px-2 py-1 rounded">npx supabase status</code></li>
                    <li>• Local API URL: <code className="bg-yellow-100 px-2 py-1 rounded">http://localhost:54321</code></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'local-testing-supabase-setup':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Supabase Local Setup</h1>
              <p className="text-lg text-text-secondary mb-6">
                Set up your local Supabase instance to test the EmailValidator API endpoints locally.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">1. Install Supabase CLI</h3>
                <CodeBlock
                  language="bash"
                  title="Installation"
                  code={`# Using npm
npm install -g supabase

# Using Homebrew (macOS)
brew install supabase/tap/supabase

# Using Scoop (Windows)
scoop bucket add supabase https://github.com/supabase/scoop-bucket.git
scoop install supabase`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">2. Start Supabase</h3>
                <CodeBlock
                  language="bash"
                  title="Start Local Instance"
                  code={`# Navigate to your project
cd /path/to/your/emailvalidator

# Start Supabase
npx supabase start

# Check status
npx supabase status`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">3. Get Local Credentials</h3>
                <CodeBlock
                  language="bash"
                  title="Get API Keys"
                  code={`npx supabase status

# Output will show:
# API URL: http://localhost:54321
# anon key: YOUR_ANON_KEY
# service_role key: YOUR_SERVICE_ROLE_KEY`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">4. Test Connection</h3>
                <CodeBlock
                  language="bash"
                  title="Test API"
                  code={`# Test with a simple curl command
curl -X POST "http://localhost:54321/functions/v1/validate-email-single" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ANON_KEY" \\
  -d '{"email": "test@example.com"}'`}
                />
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-green-900 mb-2">Success Indicators</h4>
                  <ul className="text-green-800 space-y-2">
                    <li>• Supabase status shows "Running" for all services</li>
                    <li>• API URL is accessible at <code className="bg-green-100 px-2 py-1 rounded">http://localhost:54321</code></li>
                    <li>• cURL commands return JSON responses</li>
                    <li>• No authentication errors in responses</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'local-testing-testing-guide':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Testing Guide</h1>
              <p className="text-lg text-text-secondary mb-6">
                Step-by-step guide to test all EmailValidator API endpoints locally.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Step 1: Start Supabase</h3>
                <CodeBlock
                  language="bash"
                  title="Start Local Environment"
                  code={`cd /Users/perennial/Downloads/emailvalidator
npx supabase start`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Step 2: Get Your API Key</h3>
                <CodeBlock
                  language="bash"
                  title="Get Local Credentials"
                  code={`npx supabase status

# Copy the "anon key" value for use in your requests`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Step 3: Test Single Validation</h3>
                <CodeBlock
                  language="bash"
                  title="Test Single Email"
                  code={`curl -X POST "http://localhost:54321/functions/v1/validate-email-single" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ANON_KEY" \\
  -d '{"email": "test@example.com"}'`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Step 4: Test Bulk Validation</h3>
                <CodeBlock
                  language="bash"
                  title="Test Bulk Emails"
                  code={`curl -X POST "http://localhost:54321/functions/v1/validate-email-bulk" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ANON_KEY" \\
  -d '{
    "emails": ["test@example.com", "user@gmail.com", "invalid@nonexistent.com"],
    "validationType": "bulk"
  }'`}
                />
                <div className="mt-3 p-3 bg-blue-50 rounded">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> Save the <code>validationId</code> from the response for the next step
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Step 5: Get Results</h3>
                <CodeBlock
                  language="bash"
                  title="Get Validation Results"
                  code={`curl -X GET "http://localhost:54321/functions/v1/get-validation-results?validationId=YOUR_VALIDATION_ID" \\
  -H "Authorization: Bearer YOUR_ANON_KEY"`}
                />
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Lightbulb" size={20} className="text-purple-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-purple-900 mb-2">Testing Tips</h4>
                  <ul className="text-purple-800 space-y-2">
                    <li>• Test with various email formats: valid, invalid, disposable, role-based</li>
                    <li>• Check response times and error handling</li>
                    <li>• Verify credit consumption for authenticated users</li>
                    <li>• Test rate limiting with multiple rapid requests</li>
                    <li>• Use different validation types: 'bulk', 'file_upload'</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'endpoints-single-validation':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Single Email Validation</h1>
              <p className="text-lg text-text-secondary mb-6">
                Validate a single email address using the unified endpoint. Single email validation uses the same 
                endpoint as bulk validation but with only one email in the array.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">POST</span>
                <code className="text-lg font-mono">/emailvalidator</code>
              </div>
              <p className="text-text-secondary">
                Validates a single email address and returns detailed validation results.
              </p>
            </div>

            <ParameterTable
              title="Request Parameters"
              parameters={[
                {
                  name: 'emails',
                  type: 'array',
                  required: true,
                  description: 'Array containing a single email address to validate'
                },
                {
                  name: 'validationType',
                  type: 'string',
                  required: true,
                  description: 'Must be "bulk" (same endpoint used for both single and bulk validation)'
                }
              ]}
            />

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Request Example</h3>
              <CodeBlock
                language="json"
                title="Request Body"
                code={`{
  "emails": ["test@example.com"],
  "validationType": "bulk"
}`}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">cURL Example</h3>
              <CodeBlock
                language="bash"
                title="Single Email Validation with cURL"
                code={`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsImtpZCI6IkFmazJVUno5ak5JVHI5WkgiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3Bheml3ZGR0cnh4cmVxZXdyb3lyLnN1cGFiYXNlLmNvL2F1dGgvdjEiLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMiLCJhdWQiOiJhdXRoZW50aWNhdGVkIiwiZXhwIjoxNzUzNjA5OTgzLCJpYXQiOjE3NTM2MDYzODMsImVtYWlsIjoicnVwYXJlbGl5YXJpbmFAZ21haWwuY29tIiwicGhvbmUiOiIiLCJhcHBfbWV0YWRhdGEiOnsicHJvdmlkZXIiOiJlbWFpbCIsInByb3ZpZGVycyI6WyJlbWFpbCJdfSwidXNlcl9tZXRhZGF0YSI6eyJlbWFpbCI6InJ1cGFyZWxpeWFyaW5hQGdtYWlsLmNvbSIsImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJmdWxsX25hbWUiOiJyaW5hIiwicGhvbmVfdmVyaWZpZWQiOmZhbHNlLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMifSwicm9sZSI6ImF1dGhlbnRpY2F0ZWQiLCJhYWwiOiJhYWwxIiwiYW1yIjpbeyJtZXRob2QiOiJwYXNzd29yZCIsInRpbWVzdGFtcCI6MTc1MzYwNjM4M31dLCJzZXNzaW9uX2lkIjoiNTVjOGQwYjUtODhiNS00MzNjLWE3N2MtNjIxMzdkYzQyZmY0IiwiaXNfYW5vbnltb3VzIjpmYWxzZX0.91yjOkKAzc495r6-u8ixdh6HvgsKhU6OBwZhR9HxB8M' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["test@example.com"],
    "validationType": "bulk"
}'`}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Response Example</h3>
              <CodeBlock
                language="json"
                title="Success Response"
                code={`{
  "success": true,
  "validationId": "3e083806-e2aa-4a20-83a8-7919df50d937",
  "totalEmails": 1,
  "processedEmails": 1,
  "emailResults": [
    {
      "email": "test@example.com",
      "status": "valid",
      "deliverabilityScore": 100,
      "details": "Email is valid and deliverable",
      "domain": "example.com",
      "errorType": null,
      "errorMessage": null,
      "processingTimeMs": 0
    }
  ],
  "summary": {
    "valid": 1,
    "invalid": 0,
    "risky": 0,
    "unknown": 0
  },
  "creditsUsed": 1,
  "creditsRemaining": 99,
  "creditsTotal": 100,
  "creditsUsedTotal": 1,
  "processingTime": 3853
}`}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">JavaScript Example</h3>
              <CodeBlock
                language="javascript"
                title="JavaScript Implementation"
                code={`// Single email validation using the unified endpoint
const validateSingleEmail = async (email) => {
  const response = await fetch('https://api.octalchip.com/emailvalidator', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_JWT_TOKEN',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      emails: [email],
      validationType: 'bulk'
    })
  });

  const data = await response.json();
  return data;
};

// Usage
const result = await validateSingleEmail('test@example.com');
console.log(result.emailResults[0]); // Single email result`}
              />
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-green-900 mb-2">Unified Endpoint</h4>
                  <p className="text-green-800">
                    Single email validation uses the same endpoint as bulk validation. Simply pass an array with 
                    one email address and set <code className="bg-green-100 px-1 rounded">validationType</code> to "bulk".
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'endpoints-bulk-validation':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Bulk Email Validation</h1>
              <p className="text-lg text-text-secondary mb-6">
                Validate multiple email addresses in a single request. Perfect for processing 
                large email lists efficiently with batch processing capabilities.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">POST</span>
                <code className="text-lg font-mono">/emailvalidator</code>
              </div>
              <p className="text-text-secondary">
                Validates multiple email addresses in a single request with up to 1000 emails per batch.
              </p>
            </div>

            <ParameterTable
              title="Request Parameters"
              parameters={[
                {
                  name: 'emails',
                  type: 'array',
                  required: true,
                  description: 'Array of email addresses to validate (max 1000)',
                  example: ['user1@example.com', 'user2@example.com'],
                  format: 'Array of valid email strings'
                },
                {
                  name: 'validationType',
                  type: 'string',
                  required: true,
                  description: 'Must be "bulk" for bulk validation',
                  example: 'bulk'
                }
              ]}
            />

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Request Example</h3>
              <CodeBlock
                language="json"
                title="Bulk Validation Request"
                code={`{
  "emails": [
    "john.doe@example.com",
    "jane.smith@company.org",
    "contact@business.net",
    "support@website.com",
    "info@organization.edu"
  ],
  "validationType": "bulk"
}`}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">cURL Example</h3>
              <CodeBlock
                language="bash"
                title="Bulk Email Validation with cURL"
                code={`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsImtpZCI6IkFmazJVUno5ak5JVHI5WkgiLCJ0eXAiOiJKV1QifQ.eyJpc3MiOiJodHRwczovL3Bheml3ZGR0cnh4cmVxZXdyb3lyLnN1cGFiYXNlLmNvL2F1dGgvdjEiLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMiLCJhdWQiOiJhdXRoZW50aWNhdGVkIiwiZXhwIjoxNzUzNjA5OTgzLCJpYXQiOjE3NTM2MDYzODMsImVtYWlsIjoicnVwYXJlbGl5YXJpbmFAZ21haWwuY29tIiwicGhvbmUiOiIiLCJhcHBfbWV0YWRhdGEiOnsicHJvdmlkZXIiOiJlbWFpbCIsInByb3ZpZGVycyI6WyJlbWFpbCJdfSwidXNlcl9tZXRhZGF0YSI6eyJlbWFpbCI6InJ1cGFyZWxpeWFyaW5hQGdtYWlsLmNvbSIsImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJmdWxsX25hbWUiOiJyaW5hIiwicGhvbmVfdmVyaWZpZWQiOmZhbHNlLCJzdWIiOiI5YTIzYjdmZC0wZTU4LTQ3ZjMtODZmYi1iYTkwMTkwMDBjOTMifSwicm9sZSI6ImF1dGhlbnRpY2F0ZWQiLCJhYWwiOiJhYWwxIiwiYW1yIjpbeyJtZXRob2QiOiJwYXNzd29yZCIsInRpbWVzdGFtcCI6MTc1MzYwNjM4M31dLCJzZXNzaW9uX2lkIjoiNTVjOGQwYjUtODhiNS00MzNjLWE3N2MtNjIxMzdkYzQyZmY0IiwiaXNfYW5vbnltb3VzIjpmYWxzZX0.91yjOkKAzc495r6-u8ixdh6HvgsKhU6OBwZhR9HxB8M' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Response Example</h3>
              <CodeBlock
                language="json"
                title="Bulk Validation Response"
                code={`{
  "success": true,
  "validationId": "90f77e69-f59b-4f91-9e30-28dac6cfbe39",
  "totalEmails": 5,
  "processedEmails": 5,
  "emailResults": [
    {
      "email": "john.doe@example.com",
      "status": "valid",
      "deliverabilityScore": 100,
      "details": "Email is valid and deliverable",
      "domain": "example.com",
      "errorType": null,
      "errorMessage": null,
      "processingTimeMs": 0
    },
    {
      "email": "jane.smith@company.org",
      "status": "valid",
      "deliverabilityScore": 93,
      "details": "Email is valid and deliverable",
      "domain": "company.org",
      "errorType": null,
      "errorMessage": null,
      "processingTimeMs": 0
    },
    {
      "email": "contact@business.net",
      "status": "valid",
      "deliverabilityScore": 87,
      "details": "Email is valid and deliverable",
      "domain": "business.net",
      "errorType": null,
      "errorMessage": null,
      "processingTimeMs": 0
    },
    {
      "email": "support@website.com",
      "status": "valid",
      "deliverabilityScore": 95,
      "details": "Email is valid and deliverable",
      "domain": "website.com",
      "errorType": null,
      "errorMessage": null,
      "processingTimeMs": 0
    },
    {
      "email": "info@organization.edu",
      "status": "valid",
      "deliverabilityScore": 89,
      "details": "Email is valid and deliverable",
      "domain": "organization.edu",
      "errorType": null,
      "errorMessage": null,
      "processingTimeMs": 0
    }
  ],
  "summary": {
    "valid": 5,
    "invalid": 0,
    "risky": 0,
    "unknown": 0
  },
  "creditsUsed": 5,
  "creditsRemaining": 95,
  "creditsTotal": 100,
  "creditsUsedTotal": 5,
  "processingTime": 4409
}`}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">JavaScript Example</h3>
              <CodeBlock
                language="javascript"
                title="JavaScript Implementation"
                code={`// Bulk email validation
const validateBulkEmails = async (emails) => {
  const response = await fetch('https://api.octalchip.com/emailvalidator', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_JWT_TOKEN',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      emails: emails,
      validationType: 'bulk'
    })
  });

  const data = await response.json();
  return data;
};

// Usage
const emails = [
  'john.doe@example.com',
  'jane.smith@company.org',
  'contact@business.net'
];

const result = await validateBulkEmails(emails);
console.log('Valid emails:', result.summary.valid);
console.log('Invalid emails:', result.summary.invalid);
console.log('Results:', result.emailResults);`}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-lg font-semibold text-blue-900 mb-2">Performance & Limits</h4>
                  <ul className="text-blue-800 space-y-2">
                    <li>• <strong>Maximum emails per request:</strong> 1,000</li>
                    <li>• <strong>Processing time:</strong> ~200-500ms per email</li>
                    <li>• <strong>Rate limit:</strong> 10 requests per minute per user</li>
                    <li>• <strong>Credits used:</strong> 1 credit per email validated</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'examples-javascript':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">JavaScript Examples</h1>
              <p className="text-lg text-text-secondary mb-6">
                Complete JavaScript examples for integrating EmailValidator API into your web applications 
                using modern JavaScript, Node.js, and popular frameworks.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Vanilla JavaScript (Browser)</h3>
                <CodeBlock
                  language="javascript"
                  title="Single Email Validation"
                  code={`// Single email validation
async function validateEmail(email) {
  try {
    const response = await fetch('https://api.emailvalidator.com/v1/validate', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const data = await response.json();
    
    if (data.success) {
      console.log('Validation result:', data.data);
      return data.data;
    } else {
      throw new Error(data.error || 'Validation failed');
    }
  } catch (error) {
    console.error('Error validating email:', error);
    throw error;
  }
}

// Usage
validateEmail('test@example.com')
  .then(result => {
    console.log(\`Email \${result.email} is \${result.valid ? 'valid' : 'invalid'}\`);
    console.log(\`Deliverability score: \${result.score}\`);
  })
  .catch(error => {
    console.error('Validation failed:', error.message);
  });`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Node.js with Axios</h3>
                <CodeBlock
                  language="javascript"
                  title="Node.js Implementation"
                  code={`const axios = require('axios');

class EmailValidator {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseURL = 'https://api.emailvalidator.com/v1';
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Authorization': \`Bearer \${this.apiKey}\`,
        'Content-Type': 'application/json'
      }
    });
  }

  async validateSingle(email, options = {}) {
    try {
      const response = await this.client.post('/validate', {
        email,
        ...options
      });
      return response.data;
    } catch (error) {
      throw new Error(\`Validation failed: \${error.response?.data?.error || error.message}\`);
    }
  }

  async validateBulk(emails, options = {}) {
    try {
      const response = await this.client.post('/validate/bulk', {
        emails,
        options
      });
      return response.data;
    } catch (error) {
      throw new Error(\`Bulk validation failed: \${error.response?.data?.error || error.message}\`);
    }
  }
}

// Usage
const validator = new EmailValidator('YOUR_API_KEY');

// Single validation
validator.validateSingle('test@example.com')
  .then(result => console.log('Single validation:', result))
  .catch(error => console.error(error.message));

// Bulk validation
const emails = ['user1@example.com', 'user2@gmail.com', 'invalid-email'];
validator.validateBulk(emails)
  .then(result => console.log('Bulk validation:', result))
  .catch(error => console.error(error.message));`}
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">React Hook Example</h3>
                <CodeBlock
                  language="javascript"
                  title="Custom React Hook"
                  code={`import { useState, useCallback } from 'react';

const useEmailValidator = (apiKey) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const validateEmail = useCallback(async (email) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://api.emailvalidator.com/v1/validate', {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${apiKey}\`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error(\`HTTP error! status: \${response.status}\`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Validation failed');
      }

      return data.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  return { validateEmail, loading, error };
};

// Usage in React component
function EmailValidationForm() {
  const { validateEmail, loading, error } = useEmailValidator('YOUR_API_KEY');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const validationResult = await validateEmail(email);
      setResult(validationResult);
    } catch (err) {
      console.error('Validation failed:', err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter email to validate"
        required
      />
      <button type="submit" disabled={loading}>
        {loading ? 'Validating...' : 'Validate Email'}
      </button>
      
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      
      {result && (
        <div>
          <h3>Validation Result:</h3>
          <p>Email: {result.email}</p>
          <p>Valid: {result.valid ? 'Yes' : 'No'}</p>
          <p>Score: {result.score}/100</p>
          <p>Deliverable: {result.deliverable}</p>
        </div>
      )}
    </form>
  );
}`}
                />
              </div>
            </div>
          </div>
        );

      case 'reference-error-codes':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-4">Error Codes Reference</h1>
              <p className="text-lg text-text-secondary mb-6">
                Complete reference of all error codes returned by the EmailValidator API, 
                including HTTP status codes and application-specific error messages.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">HTTP Status Codes</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-border rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                          Status Code
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                          Common Causes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b border-border">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-mono">200</span>
                        </td>
                        <td className="px-4 py-3 border-b border-border text-sm">Success</td>
                        <td className="px-4 py-3 border-b border-border text-sm text-text-secondary">
                          Request processed successfully
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b border-border">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-mono">400</span>
                        </td>
                        <td className="px-4 py-3 border-b border-border text-sm">Bad Request</td>
                        <td className="px-4 py-3 border-b border-border text-sm text-text-secondary">
                          Invalid parameters, malformed JSON, missing required fields
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b border-border">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-mono">401</span>
                        </td>
                        <td className="px-4 py-3 border-b border-border text-sm">Unauthorized</td>
                        <td className="px-4 py-3 border-b border-border text-sm text-text-secondary">
                          Missing or invalid API key
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b border-border">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-mono">403</span>
                        </td>
                        <td className="px-4 py-3 border-b border-border text-sm">Forbidden</td>
                        <td className="px-4 py-3 border-b border-border text-sm text-text-secondary">
                          Insufficient credits, plan limitations, suspended account
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b border-border">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-mono">429</span>
                        </td>
                        <td className="px-4 py-3 border-b border-border text-sm">Too Many Requests</td>
                        <td className="px-4 py-3 border-b border-border text-sm text-text-secondary">
                          Rate limit exceeded
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 border-b border-border">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm font-mono">500</span>
                        </td>
                        <td className="px-4 py-3 border-b border-border text-sm">Internal Server Error</td>
                        <td className="px-4 py-3 border-b border-border text-sm text-text-secondary">
                          Server-side error, temporary service unavailability
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Application Error Codes</h3>
                <div className="space-y-4">
                  {[
                    {
                      code: 'INVALID_EMAIL_FORMAT',
                      message: 'The provided email address has an invalid format',
                      solution: 'Ensure the email follows the standard format: user@domain.com'
                    },
                    {
                      code: 'DOMAIN_NOT_FOUND',
                      message: 'The email domain does not exist or cannot be resolved',
                      solution: 'Verify the domain name is correct and has valid DNS records'
                    },
                    {
                      code: 'NO_MX_RECORDS',
                      message: 'No MX records found for the domain',
                      solution: 'The domain may not be configured to receive emails'
                    },
                    {
                      code: 'DISPOSABLE_EMAIL',
                      message: 'The email is from a disposable email provider',
                      solution: 'Consider blocking disposable emails if not desired'
                    },
                    {
                      code: 'RATE_LIMIT_EXCEEDED',
                      message: 'API rate limit has been exceeded',
                      solution: 'Reduce request frequency or upgrade your plan'
                    },
                    {
                      code: 'INSUFFICIENT_CREDITS',
                      message: 'Not enough credits to process the request',
                      solution: 'Purchase additional credits or upgrade your plan'
                    }
                  ].map((error, index) => (
                    <div key={index} className="bg-surface border border-border rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <code className="text-sm font-mono bg-red-100 text-red-800 px-2 py-1 rounded">
                              {error.code}
                            </code>
                          </div>
                          <p className="text-sm text-text-primary mb-2">{error.message}</p>
                          <p className="text-sm text-text-secondary">
                            <strong>Solution:</strong> {error.solution}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Error Response Format</h3>
                <CodeBlock
                  language="json"
                  title="Error Response Structure"
                  code={`{
  "success": false,
  "error": {
    "code": "INVALID_EMAIL_FORMAT",
    "message": "The provided email address has an invalid format",
    "details": {
      "field": "email",
      "value": "invalid-email",
      "expected_format": "user@domain.com"
    }
  },
  "request_id": "req_1234567890abcdef",
  "timestamp": "2024-01-15T10:30:00Z"
}`}
                />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="FileText" size={48} className="text-text-secondary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">Select a Documentation Section</h3>
              <p className="text-text-secondary">
                Choose a topic from the sidebar to view detailed documentation.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="max-w-4xl">
      {getSectionContent()}
    </div>
  );
};

export default ContentSection;